import React from 'react';
import { FiUsers,  FiCheckCircle, FiClock, FiShield,  FiBarChart2, FiAlertTriangle, FiWifi } from 'react-icons/fi';

const LMSChallenges = () => {
  const challenges = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Resistance to Change from Teachers',
      description: 'Teachers who have used traditional methods for years often resist adopting new technology. They fear the learning curve, worry about added workload, or simply prefer familiar methods. Without teacher buy-in, LMS implementation fails.',
      stat: '60% of LMS implementations fail due to teacher resistance',
      solution: 'Truyon is designed to be intuitive and easy to learn. Minimal training required. Teachers can start using it immediately.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiAlertTriangle className="w-6 h-6" />,
      title: 'Poor Internet Connectivity',
      description: 'Many Indian institutions, especially in rural areas, struggle with inconsistent internet connectivity. Traditional LMS platforms require constant internet access, making them unusable when connectivity drops.',
      stat: '45% of Indian institutions face internet connectivity challenges',
      solution: 'Truyon works on low bandwidth and has offline capabilities. Content is accessible even with intermittent connectivity.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiWifi className="w-6 h-6" />,
      title: 'Infrastructure Limitations',
      description: 'Many institutions lack the computers, tablets, or smartphones needed for students to access LMS content. Infrastructure gaps make LMS adoption challenging, especially in underserved areas.',
      stat: '35% of schools lack adequate devices for LMS access',
      solution: 'Truyon works on any device—smartphones, tablets, laptops, or desktops. Students can use their existing devices.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Time Constraints for Training',
      description: 'Implementing an LMS requires time for training teachers, students, and administrators. Many institutions cannot afford to take teachers away from classes for extended training periods.',
      stat: '70% of institutions report training time as a major challenge',
      solution: 'Truyon is simple and intuitive. Teachers can start using it within minutes. No extensive training required.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Integrating with Existing Systems',
      description: 'Many institutions already use other systems for student records, attendance, or grading. Integrating the LMS with these systems is often difficult and time-consuming.',
      stat: '55% of institutions struggle with integration',
      solution: 'Truyon is an integrated ERP + LMS platform. No integration needed. Everything works together seamlessly.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Data Privacy and Security Concerns',
      description: 'Institutions worry about student data security and privacy. They need assurance that their data is protected and not misused.',
      stat: '65% of institutions cite data security as a key concern',
      solution: 'Truyon provides enterprise-grade security with encryption, access controls, and compliance with data protection laws.',
      color: 'bg-blue-50'
    }
  ];

  const strategies = [
    {
      title: 'Start with a Pilot Program',
      description: 'Begin with a small group of enthusiastic teachers. Let them test the system, provide feedback, and become champions for the LMS.'
    },
    {
      title: 'Provide Ongoing Support',
      description: 'Don\'t just train and leave. Provide continuous support to teachers as they use the LMS. Address their concerns and help them succeed.'
    },
    {
      title: 'Show Clear Benefits',
      description: 'Help teachers see how the LMS makes their job easier—less time on grading, better student engagement, and more organized content.'
    },
    {
      title: 'Involve Students Early',
      description: 'Students are often early adopters. Involve them in testing and training. They can help their teachers and peers.'
    },
    {
      title: 'Address Infrastructure Issues',
      description: 'Identify infrastructure gaps early—devices, internet, and power. Plan solutions before LMS implementation starts.'
    },
    {
      title: 'Choose the Right LMS',
      description: 'Select an LMS that is easy to use, works on low bandwidth, integrates with existing systems, and provides strong security.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Learning Management Systems are transforming education. They make learning accessible, engaging, and effective. But implementing an LMS is not without challenges. Many institutions start the journey, hit obstacles, and never fully realize the benefits.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">The challenges are real.</strong> Teacher resistance, poor connectivity, infrastructure gaps, training time, integration issues, and security concerns. But they are not insurmountable. With the right approach and the right platform, LMS implementation can succeed.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here are the top challenges in implementing LMS for schools, colleges, and coaching centres—and how to overcome them.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">60%</div>
          <div className="text-sm text-slate-500">LMS failures due to teacher resistance</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">45%</div>
          <div className="text-sm text-slate-500">Face internet connectivity challenges</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">65%</div>
          <div className="text-sm text-slate-500">Cite data security as a key concern</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Top LMS Implementation Challenges</h2>

      {/* Challenges List */}
      {challenges.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">⚠️ {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Strategies Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">How to Overcome These Challenges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {strategies.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Difficult LMS vs. Easy LMS Implementation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Difficult Implementation</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Complex platform, steep learning curve</span></li>
              <li className="flex items-start gap-2">• <span>Requires extensive training</span></li>
              <li className="flex items-start gap-2">• <span>Needs high-speed internet</span></li>
              <li className="flex items-start gap-2">• <span>Integration challenges</span></li>
              <li className="flex items-start gap-2">• <span>Teacher resistance</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Easy Implementation with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Simple, intuitive interface</span></li>
              <li className="flex items-start gap-2">• <span>Teachers start in minutes</span></li>
              <li className="flex items-start gap-2">• <span>Works on low bandwidth</span></li>
              <li className="flex items-start gap-2">• <span>Integrated ERP + LMS platform</span></li>
              <li className="flex items-start gap-2">• <span>High teacher adoption</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Indian Context */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Indian Context</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Indian institutions face unique challenges in LMS implementation—limited budgets, diverse connectivity scenarios, varying device availability, and the need to support multiple languages. An LMS designed for India must address these challenges.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Choose an LMS built for Indian conditions.</strong> Not a platform imported from another country, but one designed for Indian teachers, students, and institutions.
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Makes LMS Implementation Easy</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🚀</div>
            <h4 className="font-bold text-slate-900 text-sm">Simple Setup</h4>
            <p className="text-xs text-slate-600">Up and running in minutes</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Device-Friendly</h4>
            <p className="text-xs text-slate-600">Works on any device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Low Bandwidth</h4>
            <p className="text-xs text-slate-600">Works with limited connectivity</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">Integrated Platform</h4>
            <p className="text-xs text-slate-600">ERP + LMS in one system</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">Teacher Friendly</h4>
            <p className="text-xs text-slate-600">Minimal training required</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Secure</h4>
            <p className="text-xs text-slate-600">Enterprise-grade security</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Make LMS Implementation Easy with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Implementing an LMS is challenging, but it doesn't have to be. With the right platform, the right approach, and the right support, institutions can overcome every obstacle.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon makes LMS implementation easy. Simple setup, intuitive interface, low bandwidth optimization, and seamless integration with your existing systems. Teachers start using it within minutes. Students access it from any device.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for LMS implementation.</strong> Overcoming challenges. Delivering results.
        </p>
      </div>
    </>
  );
};

export default LMSChallenges;